/**
 * <mobile-menu-drawer>
 * Drill-down mobile navigation. Open/close/overlay/body-lock/focus-trap/Escape are delegated to
 * theme.Drawers (the same controller the cart drawer uses); this component only manages sliding
 * between the pre-rendered panels (root -> level 2 -> level 3).
 */
(function () {
  var DESKTOP_BREAKPOINT = 1008;

  class MobileMenuDrawer extends HTMLElement {
    constructor() {
      super();
      // Scope to drill panels only. NB: do not use [data-panel] here — the
      // Region/Currency disclosure popover also carries data-panel (required by
      // the LocalizationForm component) and must not be treated as a nav panel.
      this.panels = Array.from(this.querySelectorAll(".mobile-drawer__panel[data-panel-id]"));
      this.panelById = {};
      this.panels.forEach((panel) => {
        this.panelById[panel.dataset.panelId] = panel;
      });
      this.stack = ["root"];

      this.onClick = this.onClick.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.onClick);
      window.addEventListener("resize", this.onResize);

      this.render(false);
      this.initDrawerController();

      // Mirror the open state onto <html> as `mobile-menu-open`. The drawer
      // lives inside the position:sticky header section (a stacking context);
      // CSS uses this class to lift that section above the page overlay while
      // the menu is open, so the drawer isn't buried under it. Scoped to the
      // mobile menu (not the generic .js-drawer-open) so the cart drawer's
      // layering is untouched. Also resets to the root panel once closed.
      this.observer = new MutationObserver(() => {
        var isOpen = this.getAttribute("aria-hidden") === "false";
        document.documentElement.classList.toggle("mobile-menu-open", isOpen);
        if (!isOpen) {
          window.setTimeout(() => this.reset(), 500);
        }
      });
      this.observer.observe(this, { attributes: true, attributeFilter: ["aria-hidden"] });
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);
      window.removeEventListener("resize", this.onResize);
      if (this.observer) this.observer.disconnect();
    }

    initDrawerController() {
      var ready = function () {
        if (this.drawerController) return;
        if (window.theme && window.theme.Drawers) {
          this.drawerController = new window.theme.Drawers("MobileDrawer", "mobilenav");
        }
      }.bind(this);

      ready();
      if (!this.drawerController) {
        // theme.js loads with `defer`; retry once the DOM is parsed.
        document.addEventListener("DOMContentLoaded", ready, { once: true });
      }
    }

    onClick(event) {
      var drill = event.target.closest("[data-drill-target]");
      if (drill && this.contains(drill)) {
        event.preventDefault();
        this.push(drill.dataset.drillTarget);
        return;
      }

      var back = event.target.closest("[data-drill-back]");
      if (back && this.contains(back)) {
        event.preventDefault();
        this.pop();
        return;
      }

      // theme.Drawers only binds the first .js-drawer-close it finds; wire the
      // close button rendered in every panel so all of them close the drawer.
      var close = event.target.closest(".js-drawer-close");
      if (close && this.contains(close) && this.drawerController) {
        event.preventDefault();
        this.drawerController.close();
      }
    }

    onResize() {
      if (window.innerWidth >= DESKTOP_BREAKPOINT && this.drawerController && this.drawerController.isOpen) {
        this.drawerController.close();
      }
    }

    push(id) {
      if (!this.panelById[id]) return;
      if (this.stack[this.stack.length - 1] === id) return;
      this.stack.push(id);
      this.render(true);
    }

    pop() {
      if (this.stack.length <= 1) return;
      this.stack.pop();
      this.render(true);
    }

    reset() {
      if (this.stack.length === 1) return;
      this.stack = ["root"];
      this.render(false);
    }

    render(moveFocus) {
      var activeId = this.stack[this.stack.length - 1];

      this.panels.forEach((panel) => {
        // A panel is open (translateX 0) if it is in the current path; every
        // ancestor stays open underneath the active (top) panel. Only the
        // active panel is interactive — ancestors are covered, so inert them.
        var inStack = this.stack.indexOf(panel.dataset.panelId) !== -1;
        panel.classList.toggle("is-open", inStack);
        panel.toggleAttribute("inert", panel.dataset.panelId !== activeId);
      });

      var active = this.panelById[activeId];
      if (!active) return;
      active.querySelector(".mobile-drawer__body").scrollTop = 0;

      if (moveFocus) {
        var focusTarget = active.querySelector(".mobile-drawer__back, .mobile-drawer__link, a, button");
        if (focusTarget) {
          window.requestAnimationFrame(function () {
            focusTarget.focus({ preventScroll: true });
          });
        }
      }
    }
  }

  if (!customElements.get("mobile-menu-drawer")) {
    customElements.define("mobile-menu-drawer", MobileMenuDrawer);
  }
})();
